'use client'

import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import qs from 'qs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useBulkActionData } from './useFetchModalData'
import { orvalFetch, type Any } from '@api/orvalFetcher'
import {
  BulkAction,
  BulkActionFormField,
  getGetPayoutsWidgetsQueryKey,
  getListDepositsQueryKey,
  getListPayoutsQueryKey,
  type ActionFormField,
  type BulkActionFormFieldType,
} from '@apiHooks/index'
import { unassignedUserOption } from '@app/(dashboard)/payouts/PayoutsFilterConfig'
import {
  DividerField,
  MarkdownField,
  type MarkdownFieldProps,
} from '@components/DynamicActions/components/FormFieldRenderer'
import { AllFiltersArea, QueryParam } from '@components/Filters/filter.types'
import { useFilterCount } from '@components/Filters/hooks/useFilterMetadata'
import { UrlLengthDialogContent } from '@components/ui/Dialog/UrlLengthDialogContent'
import { InputWithDropdown } from '@components/ui/InputWithDropdown'
import {
  Button,
  Input,
  SingleSelect,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@fp/ui-kit'
import { backendUrl } from '@lib/constants'
import { emitDialog } from '@lib/emitDialog'
import { emitToast } from '@lib/emitToast'
import { paramsDataToFilterValues } from '@lib/filtersToFormValues'
import { BACKEND_LIMIT, formatBytes, getUrlLengthStatus } from '@lib/urlLength'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@ui/Form'

// Areas absent here invalidate nothing extra on bulk-action success.
const QUERY_KEYS: Partial<Record<AllFiltersArea, QueryKey[]>> = {
  deposits: [getListDepositsQueryKey()],
  payouts: [getListPayoutsQueryKey(), getGetPayoutsWidgetsQueryKey()],
}

interface BulkActionsModalProps {
  open: boolean
  onClose?: () => void
  action: BulkAction
  tabKey: AllFiltersArea
  selectedIds: Record<string | number, boolean>
}

type FormValues = Record<string, Any>

export const BulkActionsModal = ({ open, onClose, action, tabKey, selectedIds }: BulkActionsModalProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { data: modalData, fetchActionData } = useBulkActionData()
  const qc = useQueryClient()
  const searchParams = useSearchParams()
  const filtersCount = useFilterCount(tabKey)
  const idsCount = Object.keys(selectedIds).length
  const selectedCount = idsCount || filtersCount || 0

  const params = useMemo(() => paramsDataToFilterValues(searchParams), [searchParams])

  const { action_name } = action

  const requestParams = useMemo(
    () => ({
      filters: {
        ...params,
        ids: Object.keys(selectedIds)
          .filter((id) => selectedIds[Number(id)])
          .map(Number),
      },
    }),
    [params, selectedIds],
  )

  const requestParamsRef = useRef(requestParams)
  useEffect(() => {
    // Written in an effect (not during render) so we never touch a ref while rendering.
    requestParamsRef.current = requestParams
  }, [requestParams])

  useEffect(() => {
    if (open) {
      // TODO (y.kravchenko): take from action.path and action.method after further discussing with backend and receiving /new path in filter_metadata (now they are duplicated) (not a priority now)
      // Only include filters param when there are any filters present
      const qsString = qs.stringify(requestParamsRef.current, {
        arrayFormat: 'brackets',
        encodeValuesOnly: true,
        skipNulls: true,
      })

      const fullUrl = `/admin/api/${tabKey}/bulk_actions/new?${qsString}&action_name=${action_name}`
      const urlLength = new TextEncoder().encode(backendUrl + fullUrl).length
      const status = getUrlLengthStatus(urlLength)

      if (status === 'error') {
        emitDialog({
          title: 'URL limit exceeded for bulk actions',
          type: 'error',
          content: (
            <UrlLengthDialogContent
              status='error'
              formattedCurrent={formatBytes(urlLength)}
              formattedLimit={formatBytes(BACKEND_LIMIT)}
              percentageUsed={Math.min(Math.round((urlLength / BACKEND_LIMIT) * 100), 100)}
            />
          ),
        })
        onClose?.()

        return
      }

      fetchActionData(`/admin/api/${tabKey}/bulk_actions/new?${qsString}`, 'GET', action_name)
    }
  }, [open, fetchActionData, action_name, tabKey, onClose])

  const defaultValues = useMemo(() => {
    if (!modalData) return {}

    const values: QueryParam = {
      limit: 0,
    }

    modalData.fields.forEach((field) => {
      if (field.default !== undefined) {
        values[field.param_key || ''] = field.default
      }
    })

    return values
  }, [modalData])

  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues,
  })

  const buildPayload = useCallback(
    (values: FormValues): QueryParam => {
      const filters = idsCount
        ? {
            ids: Object.keys(selectedIds)
              .filter((id) => selectedIds[Number(id)])
              .map(Number),
          }
        : { ...params, limit: Number(values.limit) }

      const payload: QueryParam = { filters }

      for (const field of modalData?.fields || []) {
        if (!field.param_key) continue

        const value = values[field.param_key]
        if (value !== undefined && value !== null) {
          payload[field.param_key] = value
        }
      }

      if (values.assignee_id === 'null') {
        payload.assignee_id = null as unknown as number
      }

      return payload
    },
    [idsCount, modalData?.fields, params, selectedIds],
  )

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!modalData || isLoading) return
      setIsLoading(true)

      try {
        const payload = buildPayload(values)
        const response = await orvalFetch({
          url: modalData.action.path,
          method: modalData.action.method as 'GET' | 'POST' | 'PATCH' | 'DELETE',
          data: payload,
        })

        const message =
          typeof response === 'object' && response !== null && 'message' in response
            ? (response as { message: string }).message
            : undefined

        emitToast({
          message: message || 'Action completed successfully.',
          type: 'success',
        })
        QUERY_KEYS[tabKey]?.forEach((key) => qc.invalidateQueries({ queryKey: key }))

        onClose?.()
      } catch (error) {
        console.error('Error submitting bulk action:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [modalData, isLoading, buildPayload, tabKey, onClose, qc],
  )

  const renderField = (field: BulkActionFormField) => {
    const commonFieldProps = {
      control: form.control,
      name: field.param_key ?? '',
    }

    switch (field.type) {
      case 'markdown' as unknown as BulkActionFormFieldType: // TODO (nikita): remove as unknown as
        return <MarkdownField paramsField={field as MarkdownFieldProps['paramsField']} />

      case 'divider' as unknown as BulkActionFormFieldType: // TODO (nikita): remove as unknown as
        return <DividerField paramsField={field as ActionFormField} />

      case 'selector':
        return (
          <FormField
            {...commonFieldProps}
            key={`${field.param_key}-${field.type}-${field.name}`}
            rules={{ required: field.required ? `${field.name} is required` : false }}
            render={({ field: formField }) => (
              <FormItem>
                <div className='grid w-full items-center gap-2'>
                  <FormLabel>{field.name}</FormLabel>

                  <FormControl>
                    <SingleSelect
                      value={formField.value || ''}
                      onChange={(value) => formField.onChange(value)}
                      options={field.options || []}
                      placeholder={field.placeholder || 'Select...'}
                      disabled={field.disabled || isLoading}
                      modal
                    />
                  </FormControl>

                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        )
      case 'search_path':
        return (
          <FormField
            {...commonFieldProps}
            key={`${field.param_key}-${field.type}-${field.name}`}
            rules={{
              validate: (value) => {
                if (!value && value !== 'null') return `${field.name} is required`

                return true
              },
            }}
            render={({ field: formField }) => (
              <FormItem>
                <div className='grid w-full items-center gap-2'>
                  <FormLabel>{field.name}</FormLabel>
                  <FormControl>
                    <InputWithDropdown
                      onSelect={formField.onChange}
                      searchPath={field.search_path}
                      type='name'
                      placeholder={field.placeholder || 'Select...'}
                      additionalOptions={[unassignedUserOption]}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        )

      case 'number':
        return (
          <FormField
            {...commonFieldProps}
            key={`${field.param_key}-${field.type}-${field.name}`}
            rules={{
              required: field.required ? `${field.name} is required` : false,
            }}
            render={({ field: formField }) => (
              <FormItem>
                <div className='grid w-full items-center gap-2'>
                  <FormLabel>{field.name}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={field.placeholder || 'Enter number'}
                      disabled={field.disabled || isLoading}
                      {...formField}
                      onChange={(e) => formField.onChange(e.target.value ?? '')}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        )

      case 'text':
      default:
        return (
          <FormField
            {...commonFieldProps}
            key={`${field.param_key}-${field.type}-${field.name}`}
            rules={{ required: field.required ? `${field.name} is required` : false }}
            render={({ field: formField }) => (
              <FormItem>
                <div className='grid w-full items-center gap-2'>
                  <FormLabel>{field.name}</FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder={field.placeholder || 'Enter value'}
                      disabled={field.disabled || isLoading}
                      {...formField}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        )
    }
  }

  if (!modalData) return null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{modalData.modal_title}</DialogTitle>
          {modalData.modal_description && <DialogDescription>{modalData.modal_description}</DialogDescription>}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4 mt-4'>
            <div className='grid w-full items-center gap-2'>
              <FormLabel>Total results:</FormLabel>
              <Input type='text' value={selectedCount} disabled className='bg-muted' />
            </div>

            {!idsCount && (
              <FormField
                control={form.control}
                name='limit'
                defaultValue={selectedCount}
                rules={{
                  required: 'Number is required',
                  min: { value: 1, message: 'Number must be greater than 0' },
                  validate: (value) => {
                    if (!value) return true
                    const num = Number(value)
                    if (Number.isNaN(num)) return 'Must be a number'
                    if (num > selectedCount) return `Cannot exceed total (${selectedCount})`

                    return true
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <div className='grid w-full items-center gap-2'>
                      <FormLabel>Number:</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Enter number'
                          disabled={isLoading}
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ?? '')}
                        />
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            )}

            {/* Dynamic fields from modalData */}
            {modalData.fields.map(renderField)}

            <div className='flex justify-end gap-2 pt-2'>
              <Button type='submit' variant='primary' disabled={isLoading} isLoading={isLoading} className='w-full'>
                {modalData.submit_btn_txt}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
